'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Search } from 'lucide-react'

import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
} from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../../context'
import { SchoolClassSelectBase } from '../school-class-select'
import { DownloadPin } from './download-pin'
import { SharePinDialogFooter, SharePinDialogFooterError, SharePinDialogFooterLoading } from './footer'
import { KidsList } from './kids-list'

interface SharePinDialogProps {
  className?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'school' | 'web'
}

export function SharePinDialog({ className, open, onOpenChange, type }: SharePinDialogProps) {
  const { isCopied, handleCopy } = useHandleCopy()

  const [searchInput, setSearchInput] = React.useState('')
  const [schoolClassId, setSchoolClassId] = React.useState<string | null>(null)

  const authClient = useAuthClient()

  const trpc = useSharedTRPC()

  const {
    data: getOneOrganization,
    isPending: isPendingGetOneOrganization,
    error: errorGetOneOrganization,
    refetch: refetchGetOneOrganization,
  } = authClient.useActiveOrganization()

  // The class dropdown is role-aware: owner/admin see "Semua" + all classes + "Tanpa kelas";
  // a member sees only their own classes (no "Semua", no "Tanpa kelas"), and none at all if unassigned.
  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: type === 'school' && isAdminOrOwner,
  })
  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: type === 'school' && !isAdminOrOwner && !isRolePending,
  })

  const teamsQuery = isAdminOrOwner ? allTeamsQuery : myTeamsQuery
  const schoolClasses = (teamsQuery.data ?? []).map((t) => ({ id: t.id, name: t.name, image: t.image ?? null }))
  const isLoadingSchoolClasses = isRolePending || (isAdminOrOwner ? allTeamsQuery.isLoading : myTeamsQuery.isLoading)
  const isErrorSchoolClasses = teamsQuery.isError
  const isMemberWithoutClass =
    !isRolePending && !isAdminOrOwner && !isLoadingSchoolClasses && schoolClasses.length === 0

  const {
    data: getAllChildren,
    isPending: isPendingGetAllChildren,
    isError: isErrorGetAllChildren,
    refetch: refetchGetAllChildren,
  } = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
    }),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:w-full srd:h-fit srd:max-h-[95dvh] srd:overflow-y-auto srd:flex srd:flex-col srd:gap-y-8 srd:text-neutrals-black srd:max-w-md'>
        <DialogHeader>
          <DialogTitle className='srd:text-lg srd:font-semibold srd:text-left'>
            Panduan login {type === 'school' ? 'murid' : 'anak'}
          </DialogTitle>
          <DialogDescription className='srd:sr-only'>
            Panduan login {type === 'school' ? 'murid' : 'anak'}
          </DialogDescription>
        </DialogHeader>

        <div className={cn('srd:flex srd:flex-col srd:gap-y-6', className)}>
          <section className='srd:flex srd:flex-col srd:gap-y-2'>
            <p className='srd:text-sm srd:font-medium'>
              1. Pandu {type === 'web' ? 'anak' : 'murid'} kunjungi login {type === 'web' ? 'anak' : 'murid'}:
            </p>

            <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-neutrals-n-35 srd:rounded-md srd:flex-nowrap'>
              <p className='srd:w-full srd:flex-1 srd:text-neutrals-black srd:text-sm srd:font-normal srd:truncate srd:line-clamp-1'>
                {buildUrl('account', '/anak').href}
              </p>

              <Button
                variant='link'
                className='srd:p-0 srd:h-fit srd:w-fit'
                onClick={() => handleCopy(buildUrl('account', '/anak').href, 'url')}
                disabled={isCopied('url')}
              >
                {isCopied('url') ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
              </Button>
            </div>
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-2'>
            <p className='srd:text-sm srd:font-medium'>2. Masukkan Kode Aksesmu:</p>

            {(() => {
              if (errorGetOneOrganization) {
                return (
                  <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-main-red-50 srd:bg-main-red-10 srd:rounded-md srd:flex-nowrap'>
                    <p className='srd:w-full srd:flex-1 srd:text-main-red-100 srd:text-xs srd:font-medium srd:truncate srd:line-clamp-1'>
                      Gagal memuat data. Silakan coba lagi.
                    </p>
                    <Button
                      variant='outline'
                      size='sm'
                      className='srd:text-xs srd:h-6'
                      onClick={() => refetchGetOneOrganization()}
                    >
                      Coba lagi
                    </Button>
                  </div>
                )
              }

              if (isPendingGetOneOrganization || !getOneOrganization) {
                return <Skeleton className='srd:rounded-md srd:w-full srd:h-8' />
              }

              return (
                <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-neutrals-n-35 srd:rounded-md srd:flex-nowrap'>
                  <p className='srd:w-full srd:flex-1 srd:text-neutrals-black srd:text-base srd:font-semibold srd:truncate srd:line-clamp-1'>
                    {getOneOrganization.pin}
                  </p>

                  <Button
                    variant='link'
                    className='srd:p-0 srd:h-fit srd:w-fit'
                    onClick={() => handleCopy(getOneOrganization.pin ?? '', 'pin')}
                    disabled={isCopied('pin')}
                  >
                    {isCopied('pin') ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
                  </Button>
                </div>
              )
            })()}
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-2'>
            <p className='srd:text-sm srd:font-medium'>3. Masukkan PIN {type === 'web' ? 'Anak' : 'Murid'}:</p>

            {(() => {
              if (isPendingGetAllChildren) {
                return (
                  <>
                    <Skeleton className='srd:rounded-md srd:w-full srd:h-10' />

                    <Skeleton className='srd:rounded-md srd:w-full srd:h-20' />
                  </>
                )
              }

              if (isErrorGetAllChildren) {
                return (
                  <div className='srd:flex srd:flex-col srd:gap-2 srd:rounded-md srd:border srd:border-main-red-50 srd:bg-main-red-10 srd:p-4 srd:text-sm'>
                    <p className='srd:text-main-red-100'>Gagal memuat data. Silakan coba lagi.</p>
                    <Button variant='outline' size='sm' onClick={() => refetchGetAllChildren()}>
                      Coba lagi
                    </Button>
                  </div>
                )
              }

              if (type === 'web') {
                const filteredKids = getAllChildren.data.filter((student) => {
                  const isThisSearch =
                    searchInput.length > 0 ? student.name.toLowerCase().includes(searchInput.toLowerCase()) : true

                  return isThisSearch
                })

                return (
                  <>
                    <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-2 srd:flex-nowrap srd:w-full'>
                      <InputGroup className='srd:h-8 srd:rounded-full srd:flex-1 srd:w-full'>
                        <InputGroupAddon align='inline-start'>
                          <Search className='srd:text-main-blue-100 srd:size-4' />
                        </InputGroupAddon>
                        <InputGroupInput
                          placeholder='Cari...'
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          disabled={isPendingGetAllChildren}
                        />
                      </InputGroup>

                      <DownloadPin type='web' data={getAllChildren.data} />
                    </section>

                    <KidsList data={filteredKids} />
                  </>
                )
              }

              if (isLoadingSchoolClasses) {
                return (
                  <>
                    <Skeleton className='srd:rounded-md srd:w-full srd:h-10' />

                    <Skeleton className='srd:rounded-md srd:w-full srd:h-20' />
                  </>
                )
              }

              if (isErrorSchoolClasses) {
                return (
                  <div className='srd:flex srd:flex-col srd:gap-2 srd:rounded-md srd:border srd:border-main-red-50 srd:bg-main-red-10 srd:p-4 srd:text-sm'>
                    <p className='srd:text-main-red-100'>Gagal memuat data kelas. Silakan coba lagi.</p>
                    <Button variant='outline' size='sm' onClick={() => teamsQuery.refetch()}>
                      Coba lagi
                    </Button>
                  </div>
                )
              }

              const filteredKids = getAllChildren.data.filter((student) => {
                // Member without any class manages no students → show none.
                if (isMemberWithoutClass) return false
                const isThisClass =
                  schoolClassId === NO_SCHOOL_CLASS_ID
                    ? !student.schoolClass
                    : schoolClassId
                      ? student.schoolClasses?.some((sc) => sc.id === schoolClassId)
                      : true
                const isThisSearch =
                  searchInput.length > 0 ? student.name.toLowerCase().includes(searchInput.toLowerCase()) : true

                return isThisClass && isThisSearch
              })

              return (
                <>
                  <section className='srd:flex srd:items-center srd:justify-between srd:gap-2 srd:flex-wrap srd:w-full'>
                    <InputGroup className='srd:data-[size=default]:h-8 srd:flex-1 srd:w-full srd:min-w-[180px]'>
                      <InputGroupAddon align='inline-start'>
                        <Search className='srd:text-main-blue-100 srd:size-4' />
                      </InputGroupAddon>
                      <InputGroupInput
                        placeholder='Cari...'
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        disabled={isLoadingSchoolClasses}
                      />
                    </InputGroup>

                    <SchoolClassSelectBase
                      value={schoolClassId}
                      onValueChange={setSchoolClassId}
                      teams={schoolClasses}
                      size='sm'
                      showNoClass={isAdminOrOwner}
                      allowAll={isAdminOrOwner}
                      showDash={!isAdminOrOwner}
                      className='srd:max-w-[140px] srd:h-8'
                    />

                    <DownloadPin type='school' data={getAllChildren.data} />
                  </section>

                  <KidsList data={filteredKids} />
                </>
              )
            })()}
          </section>
        </div>

        {(() => {
          if (isPendingGetOneOrganization || !getOneOrganization) {
            return <SharePinDialogFooterLoading />
          }

          if (errorGetOneOrganization) {
            return <SharePinDialogFooterError refetch={refetchGetOneOrganization} />
          }

          return <SharePinDialogFooter orgId={getOneOrganization.id} type={type} />
        })()}
      </DialogContent>
    </Dialog>
  )
}
